import React, { useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAuth, signInAnonymously, signOut as firebaseSignOut } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { Form, Button, Alert, Modal, Spinner } from 'react-bootstrap';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import { useFirestore } from '../contexts/FirestoreContext';
import ReCAPTCHA from 'react-google-recaptcha';
import { sendInternalEmail } from '../utils/mailUtils';

const PASSWORD_MIN_LENGTH = 8;

const escapeHtml = (s = '') =>
  String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[c]));

const checkIfPasswordExpired = (timestamp) => {
  if (!timestamp) {
    return false;
  }
  const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
  const now = new Date();
  const dt = typeof timestamp?.toDate === 'function' ? timestamp.toDate() : new Date(timestamp);
  return now - dt > ninetyDaysMs;
};

function LoginPanel({ showHeading = true, className = '', style }) {
  const emailRef = useRef();
  const passwordRef = useRef();
  const newPasswordRef = useRef();
  const recaptchaRef = useRef(null);

  const { login, logout } = useAuth();
  const navigate = useNavigate();
  const { updateData, getDocument } = useFirestore();

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [updatingPassword, setUpdatingPassword] = useState(false);
  const [privacyPolicy, setPrivacyPolicy] = useState(false);
  const [termsCond, setTermsCond] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');
  const [modalTermsTitle, setModalTermsTitle] = useState('');

  const [showRequestModal, setShowRequestModal] = useState(false);
  const [reqFullName, setReqFullName] = useState('');
  const [reqCompany, setReqCompany] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [requestMsg, setRequestMsg] = useState(null);

  const mergedStyle = { maxWidth: '500px', ...(style || {}) };

  const handleShowTermsModal = (url) => {
    setModalTermsTitle(url === '/PrivacyStatement.pdf' ? 'Privacy Statement' : 'Terms and Conditions');
    setPdfUrl(url);
    setShowTermsModal(true);
  };

  const handleCloseTermsModal = () => {
    setShowTermsModal(false);
    setPdfUrl('');
    setModalTermsTitle('');
  };

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      const authInstance = getAuth();
      const signedInUser = await login(emailRef.current?.value || '', passwordRef.current?.value || '');
      const user = signedInUser ?? authInstance.currentUser;

      if (!user) {
        throw new Error('Login succeeded, but user session is not ready. Please try again.');
      }

      const idTokenResult = await user.getIdTokenResult(true);
      const nextRole = idTokenResult.claims.role;
      let userData = null;

      try {
        userData = await getDocument('users', user.uid);
      } catch (docErr) {
        console.warn('No Firestore user document found for login user:', docErr);
      }

      const passwordExpired = checkIfPasswordExpired(userData?.dtPasswordChanged);

      if (idTokenResult.claims.mustChangePassword || passwordExpired) {
        setPrivacyPolicy(Boolean(userData?.privacyPolicy));
        setTermsCond(Boolean(userData?.termsConditions));
        setShowPasswordModal(true);
      } else if (nextRole === 'Customer') {
        navigate('/customer');
      } else if (nextRole === 'Upfitter') {
        navigate('/upfitter');
      } else if (nextRole === 'Admin') {
        navigate('/salesorders');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err?.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordUpdate() {
    const newPassword = newPasswordRef.current?.value || '';

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters long.`);
      return;
    }

    const authInstance = getAuth();
    const user = authInstance.currentUser;
    if (!user) {
      setError('No authenticated user found.');
      return;
    }

    const idTokenResult = await user.getIdTokenResult(true);
    const userRole = idTokenResult.claims.role;

    setUpdatingPassword(true);

    try {
      const updateUser = httpsCallable(functions, 'updateUser');
      await updateUser({
        uid: user.uid,
        password: newPassword,
        mustChangePassword: false,
        role: userRole,
      });

      let userData = null;
      try {
        userData = await getDocument('users', user.uid);
      } catch (docErr) {
        console.warn('Unable to load user document for password update:', docErr);
      }

      if (userData) {
        userData.privacyPolicy = true;
        userData.termsConditions = true;
        userData.dtPasswordChanged = Timestamp.now();
        await updateData('users', user.uid, userData);
      }

      setShowPasswordModal(false);
      setPrivacyPolicy(false);
      setTermsCond(false);

      if (userRole === 'Customer') {
        navigate('/customer');
      } else if (userRole === 'Admin') {
        navigate('/salesorders');
      } else if (userRole === 'Upfitter') {
        navigate('/upfitter');
      } else {
        navigate('/');
      }
    } catch (err) {
      console.error('Failed to update password:', err);
      setError('Failed to update password: ' + (err?.message || 'Unknown error'));
    } finally {
      setUpdatingPassword(false);
    }
  }

  async function handleModalClose() {
    setShowPasswordModal(false);
    setPrivacyPolicy(false);
    setTermsCond(false);
    await logout();
    navigate('/');
  }

  function openRequestModal() {
    setReqEmail(emailRef.current?.value || '');
    setReqFullName('');
    setReqCompany('');
    setRequestMsg(null);
    setError('');
    setShowRequestModal(true);
  }

  function validateRequestForm() {
    if (!reqFullName.trim() || !reqCompany.trim() || !reqEmail.trim()) {
      setRequestMsg({ type: 'danger', text: 'Please fill out all fields.' });
      return false;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(reqEmail);
    if (!emailOk) {
      setRequestMsg({ type: 'danger', text: 'Please enter a valid email.' });
      return false;
    }
    return true;
  }

  async function submitRequest() {
    if (!validateRequestForm()) return;

    setRequesting(true);
    setRequestMsg(null);

    try {
      const token = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();

      const fullName = reqFullName.trim();
      const company = reqCompany.trim();
      const email = reqEmail.trim().toLowerCase();

      const requestAccount = httpsCallable(functions, 'requestAccount');
      await requestAccount({
        fullName,
        company,
        email,
        recaptchaToken: token,
      });

      const authInstance = getAuth();
      let signedInTemporarily = false;

      if (!authInstance.currentUser) {
        try {
          await signInAnonymously(authInstance);
          signedInTemporarily = true;
        } catch (anonErr) {
          console.warn('Anonymous sign-in failed; will skip email notification.', anonErr);
        }
      }
/*
      try {
        if (authInstance.currentUser) {
          const safeName = escapeHtml(fullName);
          const safeCompany = escapeHtml(company);
          const safeEmail = escapeHtml(email);
          const submittedAt = escapeHtml(new Date().toLocaleString());
          const mailtoEmail = encodeURIComponent(email);
          const replyEmail = `info@dahlhauser.net`;

          await sendInternalEmail({
            toRecipients: ['mark@dahlhauser.net'],
            subject: `New Login Request: ${fullName} (${company})`,
            htmlBody: `
              <p>Hi Team,</p>
              <p><strong>${safeName}</strong> from <strong>${safeCompany}</strong> has requested access to the Fleet Sales System portal.</p>
              <p>Email: <a href="mailto:${mailtoEmail}">${safeEmail}</a></p>
              <p>Submitted: ${submittedAt}</p>
            `,
            replyTo: replyEmail,
          });
        } else {
          console.warn('Skipping login request email; no authenticated user available.');
        }
      } catch (mailErr) {
        console.error('Failed to send login request email:', mailErr);
      } finally {
        if (signedInTemporarily && authInstance.currentUser?.isAnonymous) {
          try {
            await firebaseSignOut(authInstance);
          } catch (signOutErr) {
            console.warn('Failed to sign out temporary anonymous user:', signOutErr);
          }
        }
      }
*/
     setRequestMsg({
        type: 'success',
        text: 'Request submitted. An administrator will review and contact you.',
      });
      setReqFullName('');
      setReqCompany('');
      // keep email for convenience
    } catch (err) {
      console.error(err);
      setRequestMsg({
        type: 'danger',
        text: err?.message || 'Could not submit your request. Please try again.',
      });
    } finally {
      setRequesting(false);
    }
  }

  return (
    <>
      <div className={`w-100 ${className}`.trim()} style={mergedStyle}>
        {showHeading && <h2 className="text-center mb-4">Sign In</h2>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3" controlId="email">
            <Form.Label>Email address</Form.Label>
            <Form.Control type="email" ref={emailRef} required />
          </Form.Group>
          <Form.Group className="mb-3" controlId="password">
            <Form.Label>Password</Form.Label>
            <Form.Control type="password" ref={passwordRef} required />
          </Form.Group>

          <div className="d-flex gap-2">
            <Button type="submit" disabled={loading}>
              {loading ? 'Logging in…' : 'Login'}
            </Button>
            <Button variant="outline-secondary" type="button" onClick={openRequestModal}>
              Request an account
            </Button>
          </div>
        </Form>
      </div>

      <Modal show={showPasswordModal} onHide={handleModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Update Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form.Group controlId="newPassword">
            <Form.Label>New Password</Form.Label>
            <Form.Control
              type="password"
              ref={newPasswordRef}
              required
              placeholder={`Must be at least ${PASSWORD_MIN_LENGTH} characters`}
            />
          </Form.Group>
          <Form.Group controlId="agreeToPolicy" className="mt-3">
            <Form.Check
              type="checkbox"
              label={
                <>
                  I agree to the{' '}
                  <span
                    className="text-primary text-decoration-underline"
                    role="button"
                    onClick={() => handleShowTermsModal('/PrivacyStatement.pdf')}
                  >
                    Privacy Policy
                  </span>
                </>
              }
              checked={privacyPolicy}
              onChange={(e) => setPrivacyPolicy(e.target.checked)}
            />
          </Form.Group>
          <Form.Group controlId="agreeToTC" className="mt-3">
            <Form.Check
              type="checkbox"
              label={
                <>
                  I agree to the{' '}
                  <span
                    className="text-primary text-decoration-underline"
                    role="button"
                    onClick={() => handleShowTermsModal('/TermsAndConditions.pdf')}
                  >
                    Terms and Conditions
                  </span>
                </>
              }
              checked={termsCond}
              onChange={(e) => setTermsCond(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handlePasswordUpdate}
            disabled={!privacyPolicy || !termsCond || updatingPassword}
          >
            {updatingPassword ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Updating...
              </>
            ) : 'Update Password'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showTermsModal} onHide={handleCloseTermsModal} size="xl" centered>
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: '18px', textAlign: 'center', width: '100%' }}>
            {modalTermsTitle}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <iframe
            src={pdfUrl}
            title="PDF Viewer"
            style={{ width: '100%', height: '500px', border: 'none' }}
          />
        </Modal.Body>
      </Modal>

      <Modal show={showRequestModal} onHide={() => setShowRequestModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Request an account</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {requestMsg && <Alert variant={requestMsg.type}>{requestMsg.text}</Alert>}

          <Form>
            <Form.Group className="mb-3" controlId="reqFullName">
              <Form.Label>Full name</Form.Label>
              <Form.Control
                type="text"
                value={reqFullName}
                onChange={(e) => setReqFullName(e.target.value)}
                placeholder="Jane Doe"
                disabled={requesting}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="reqCompany">
              <Form.Label>Company</Form.Label>
              <Form.Control
                type="text"
                value={reqCompany}
                onChange={(e) => setReqCompany(e.target.value)}
                placeholder="Acme Corp"
                disabled={requesting}
                required
              />
            </Form.Group>
            <Form.Group className="mb-0" controlId="reqEmail">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={reqEmail}
                onChange={(e) => setReqEmail(e.target.value)}
                placeholder="name@company.com"
                disabled={requesting}
                required
              />
            </Form.Group>
          </Form>

          <ReCAPTCHA
            ref={recaptchaRef}
            size="invisible"
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
            onErrored={() => setRequestMsg({ type: 'danger', text: 'reCAPTCHA error. Please try again.' })}
            onExpired={() => recaptchaRef.current?.reset()}
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowRequestModal(false)} disabled={requesting}>
            Close
          </Button>
          <Button variant="primary" onClick={submitRequest} disabled={requesting}>
            {requesting ? (
              <>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" /> Submitting…
              </>
            ) : 'Submit request'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default LoginPanel;
